import { GiftChoiceUnitData } from '@shared/api/giftChoiceUnit/giftChoiceUnit.interface';
import { GiftDeliveryCategoryData } from '@shared/api/giftDeliveryCategory/giftDeliveryCategory.interface';
import { GiftItemBrand } from '@shared/api/giftItemBrand/giftItemBrand.interface';
import { Image } from '@shared/api/media/media.interface';
import { QueryDataState } from '@shared/interfaces';

// Enum
export enum GiftItemInfoType {
  Delivery = 'Delivery', // 배송형
  Coupon = 'Coupon', // 쿠폰형
}

// 쿠폰 상세 정보
export type CouponDetail = {
  validPeriod: string; // 유효기간
  placeOfExchange: string; // 교환처
};

// 배송 상세 정보
export type DeliveryDetail = {
  shippingInfo?: string; // 배송정보/기간 안내
  shippingFeeInfo?: string; // 배송비 안내
  shippingFee?: number; // 배송비 (원). 예: 500
  freeShippingMinQuantity?: number; // 이 수량 이상이면 무료배송. 예: 20
};

// 추가옵션 (배송형 답례품) — 선택종류별 추가금이 붙는 옵션
// 읽기는 descriptionImage가 populated(Image), 쓰기(Create/Update)는 image id(string)
export type OptionChoice<TImage = string> = {
  name: string; // 선택종류명 (예: "골든", "실버")
  priceDelta: number; // 추가금액 (원). 0이면 무료
  description?: string; // 한 줄 설명 (없을 수 있음)
  colorHex?: string; // 색상 핵사코드 (예: "#D4AF37") - 색상칩 표시용 (없을 수 있음)
  descriptionImage?: TImage; // 이 선택종류 설명 이미지 (없을 수 있음)
  order?: number; // 표시 순서
  enabled?: boolean; // false면 노출/선택 불가
};

export type AdditionalOption<TImage = string> = {
  name: string; // 추가옵션명 (예: "비누종류")
  required?: boolean; // true면 반드시 선택해야 함
  selectionCount?: number; // 선택 횟수(슬롯 수), 기본 1. 2면 같은 옵션을 2번 고름 (비누종류1/2)
  slotLabels?: string[]; // 슬롯별 라벨 ["비누종류1","비누종류2"] (없으면 name+번호로 생성)
  order?: number; // 표시 순서 (낮을수록 앞, 없으면 뒤)
  choices: OptionChoice<TImage>[]; // 정의는 1번만 — 모든 슬롯이 공유
};

// GiftItemInfo (원상품 정보 - Admin 관리)
export type GiftItemInfo<
  TImage = string,
  THeaderImages = string[],
  TDetailImages = string[],
  TDeliveredImageList = string[],
  TGiftItemBrand = string,
  TChoiceUnits = string[],
  TGiftDeliveryCategory = string,
> = {
  _id: string;
  type: GiftItemInfoType;
  giftItemBrand?: TGiftItemBrand; // 상품 브랜드 (예: 스타벅스)
  giftDeliveryCategory?: TGiftDeliveryCategory; // 배송 카테고리
  subCategory?: string; // 선택한 하위분류 이름 (카테고리.subCategories 중 하나)
  additionalOptions?: AdditionalOption<TImage>[]; // 추가옵션 (배송형)
  name: string;
  image?: TImage; // 대표 이미지 (목록 썸네일)
  headerImages: THeaderImages; // 상세페이지 상단 carousel 이미지들 (정사각형)
  detailImages: TDetailImages; // 상세 이미지 (세로로 이어붙임)
  deliveredImageList: TDeliveredImageList;
  detailInfo: string;
  priceRegular: number; // 정가
  priceRetail: number; // 판매가
  priceWhole: number; // 도매가
  isEnabled: boolean;
  isSelectable: boolean; // 선택형 상품 여부
  choiceUnits: TChoiceUnits; // 선택 가능 유닛 목록
  couponDetail?: CouponDetail; // 쿠폰형일 때 사용
  deliveryDetail?: DeliveryDetail; // 배송형일 때 사용 (상품별 오버라이드)
  allowIndividualDelivery?: boolean; // false면 개별배송 불가 (일괄배송 전용). undefined/true면 둘 다 가능
  useThanksPrint?: boolean; // 감사카드 사용 여부 오버라이드. undefined = 브랜드(thanksPrint.enabled) 따름
  thanksPrintTemplates?: string[]; // 이 상품에서 허용하는 브랜드 감사카드 템플릿 _id 목록 (빈 배열/미설정 = 브랜드 전체 허용)
  minOrderQuantity: number; // 최소주문수량
  orderUnit: number; // 주문단위
  order?: number; // 기본 표시 순서 (낮을수록 앞, 없으면 맨 뒤)
  productCode?: string;
  isTaxable: boolean; // 과세여부
  tags: string[];
  createdAt: Date;
  updatedAt: Date;
};

// 타입 별칭
export type GiftItemInfoData = GiftItemInfo<
  Image,
  Image[],
  Image[],
  Image[],
  GiftItemBrand,
  GiftChoiceUnitData[],
  GiftDeliveryCategoryData
>;
export type GiftItemInfoListData = GiftItemInfoData[];

// State 타입
export type GiftItemInfoState = QueryDataState<GiftItemInfoData>;
export type GiftItemInfoListState = QueryDataState<GiftItemInfoListData>;

// Create/Update 타입
export type GiftItemInfoCreate = {
  type: GiftItemInfoType;
  giftItemBrand?: string; // 상품 브랜드 ID
  giftDeliveryCategory?: string; // 배송 카테고리 ID
  subCategory?: string | null; // 하위분류 이름 (null로 제거)
  additionalOptions?: AdditionalOption<string>[]; // 추가옵션 (descriptionImage는 이미지 ID)
  name: string;
  image?: string;
  headerImages?: string[];
  detailImages?: string[];
  deliveredImageList?: string[];
  detailInfo?: string;
  priceRegular?: number;
  priceRetail?: number;
  priceWhole: number;
  isEnabled: boolean;
  isSelectable?: boolean; // 선택형 상품 여부
  choiceUnits?: string[]; // 선택 가능 유닛 ID 목록
  couponDetail?: CouponDetail;
  deliveryDetail?: DeliveryDetail;
  allowIndividualDelivery?: boolean; // false면 개별배송 불가 (일괄배송 전용)
  useThanksPrint?: boolean | null; // 감사카드 사용 오버라이드 (null = 브랜드 따름으로 초기화)
  thanksPrintTemplates?: string[]; // 허용 감사카드 템플릿 _id 목록 (빈 배열 = 브랜드 전체 허용)
  minOrderQuantity?: number;
  orderUnit?: number;
  order?: number; // 기본 표시 순서
  isTaxable?: boolean; // 과세여부
  productCode?: string;
  tags?: string[];
};

export type GiftItemInfoUpdate = {
  _id: string;
  type?: GiftItemInfoType;
  giftItemBrand?: string | null; // 상품 브랜드 ID (null로 설정하면 제거)
  giftDeliveryCategory?: string | null; // 배송 카테고리 ID (null로 설정하면 제거)
  subCategory?: string | null; // 하위분류 이름 (null로 제거)
  additionalOptions?: AdditionalOption<string>[]; // 추가옵션 (descriptionImage는 이미지 ID)
  name?: string;
  image?: string;
  headerImages?: string[];
  detailImages?: string[];
  deliveredImageList?: string[];
  detailInfo?: string;
  priceRegular?: number;
  priceRetail?: number;
  priceWhole?: number;
  isEnabled?: boolean;
  isSelectable?: boolean; // 선택형 상품 여부
  choiceUnits?: string[]; // 선택 가능 유닛 ID 목록
  couponDetail?: CouponDetail;
  deliveryDetail?: DeliveryDetail;
  allowIndividualDelivery?: boolean; // false면 개별배송 불가 (일괄배송 전용)
  useThanksPrint?: boolean | null; // 감사카드 사용 오버라이드 (null = 브랜드 따름으로 초기화)
  thanksPrintTemplates?: string[]; // 허용 감사카드 템플릿 _id 목록 (빈 배열 = 브랜드 전체 허용)
  minOrderQuantity?: number;
  orderUnit?: number;
  order?: number; // 기본 표시 순서
  isTaxable?: boolean; // 과세여부
  productCode?: string;
  tags?: string[];
};
